// Collapsible "Suppliers" section for the family workspace screen — lists
// which suppliers currently carry variants under this family, how many
// products each supplier has, and how much of the family's variant surface
// they cover. Lazy: `useGetFamilySuppliersApi` only fires once the panel has
// been expanded at least once, so visiting the workspace never pays for a
// request most sessions won't need. The hook itself already toasts errors
// once and disables retries (see use-get-family-suppliers-api.ts).

import { FamilySupplierRow, useGetFamilySuppliersApi } from 'apis/master-product-families';
import type { MasterProduct } from 'apis/master-products';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Icon,
  IconButtonWithTooltip,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from 'design-system';
import { useState } from 'react';
import { FiList } from 'react-icons/fi';
import { SupplierCoverageModal } from './supplier-coverage-modal';

export interface FamilySuppliersPanelProps {
  familyId: number;
  // brand · spec · packagingSummary label per variant id, built by the
  // screen from the same `variants` list that feeds the grid.
  variantLabelById: Map<number, string>;
  // Raw family variants (same list that feeds the grid) — threaded through
  // for the coverage modal, which needs full variant metadata (state,
  // isActive) rather than just the flattened label map above.
  variants: MasterProduct[];
}

// Cap the tooltip list so a supplier covering dozens of variants doesn't
// render a screen-height tooltip.
const MAX_TOOLTIP_VARIANTS = 10;

export const FamilySuppliersPanel = ({ familyId, variantLabelById, variants }: FamilySuppliersPanelProps) => {
  // Sticky once true — an uncontrolled Accordion collapsing/expanding again
  // (or a background refetch) must never re-gate the query back off.
  const [hasExpanded, setHasExpanded] = useState(false);
  const [coverageSupplier, setCoverageSupplier] = useState<FamilySupplierRow | null>(null);

  const { data, isLoading } = useGetFamilySuppliersApi(familyId, { enabled: hasExpanded });
  const suppliers = data?.suppliers ?? [];
  const totalVariants = data?.totalVariants ?? 0;

  return (
    <>
      <Accordion
        allowToggle
        onChange={(expandedIndex) => {
          if (expandedIndex !== -1) setHasExpanded(true);
        }}>
        <AccordionItem bg='white' border='1px solid' borderColor='gray.200' rounded='md'>
          <AccordionButton _expanded={{ bg: 'gray.50' }} py='3'>
            <AccordionIcon mr='2' />
            <Text flex='1' textAlign='left' fontWeight='semibold'>
              Suppliers{data ? ` (${suppliers.length})` : ''}
            </Text>
          </AccordionButton>
          <AccordionPanel pb='4'>
            {isLoading ? (
              <Center py='4'>
                <Spinner size='sm' color='red.500' />
              </Center>
            ) : suppliers.length === 0 ? (
              <Text color='gray.500' fontSize='sm' py='2'>
                No suppliers link to this family yet.
              </Text>
            ) : (
              <Box overflowX='auto'>
                <Table size='sm' variant='simple'>
                  <Thead>
                    <Tr>
                      <Th>Supplier</Th>
                      <Th>Products</Th>
                      <Th>Coverage</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {suppliers.map((supplier) => {
                      const coveredLabels = supplier.variantIds
                        .slice(0, MAX_TOOLTIP_VARIANTS)
                        .map((variantId) => variantLabelById.get(variantId) ?? `#${variantId}`);

                      return (
                        <Tr key={supplier.id}>
                          <Td>
                            <Text as='span'>{supplier.name || '—'}</Text>
                            {!supplier.isActive && (
                              <Tag size='sm' colorScheme='red' ml='2'>
                                Inactive
                              </Tag>
                            )}
                          </Td>
                          <Td>{supplier.productsCount}</Td>
                          <Td>
                            <Tooltip
                              label={coveredLabels.join('\n')}
                              whiteSpace='pre-line'
                              hasArrow
                              placement='top'
                              isDisabled={coveredLabels.length === 0}>
                              <Text as='span' cursor='default'>
                                {supplier.variantIds.length}/{totalVariants} variants
                              </Text>
                            </Tooltip>
                          </Td>
                          <Td textAlign='right'>
                            <IconButtonWithTooltip
                              size='xs'
                              variant='ghost'
                              label='View variant coverage'
                              aria-label='view-supplier-coverage-btn'
                              Icon={<Icon as={FiList} />}
                              onClick={() => setCoverageSupplier(supplier)}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      {coverageSupplier && (
        <SupplierCoverageModal
          isOpen
          onClose={() => setCoverageSupplier(null)}
          supplierName={coverageSupplier.name || 'Supplier'}
          coveredVariantIds={coverageSupplier.variantIds}
          variants={variants}
        />
      )}
    </>
  );
};
